<template>
  <div class="teams-assistente" :class="themeClass">
    <div v-if="carregandoInicial" class="teams-assistente__status ui container">
      <div class="ui active centered inline loader"></div>
      <p>Conectando ao assistente...</p>
    </div>

    <div v-else-if="acessoNegado" class="teams-assistente__status ui container">
      <div class="ui raised segment status-card">
        <h2 class="ui header">
          <i class="lock icon red"></i>
          <div class="content">
            Acesso nao autorizado
            <div class="sub header">{{ mensagemAcesso }}</div>
          </div>
        </h2>
        <p v-if="teamsContext.userEmail" class="ui text">
          E-mail Teams: <strong>{{ teamsContext.userEmail }}</strong>
        </p>
      </div>
    </div>

    <div v-else-if="sessaoEncerrada" class="teams-assistente__status ui container">
      <div class="ui raised segment status-card">
        <p>Sessao encerrada.</p>
        <button class="ui primary button" type="button" @click="recarregarAba">
          Entrar novamente
        </button>
      </div>
    </div>

    <div v-else-if="!teamsPronto" class="teams-assistente__status ui container">
      <div class="ui raised segment status-card">
        <h2 class="ui header">
          <i class="microsoft icon blue"></i>
          <div class="content">
            Abra pelo Microsoft Teams
            <div class="sub header">Esta aba so funciona dentro do app Teams.</div>
          </div>
        </h2>
      </div>
    </div>

    <div v-else-if="autenticado" class="teams-assistente__chat">
      <aside v-show="painelConversasAberto" class="teams-assistente__sidebar">
        <div class="sidebar-header">
          <strong>Conversas</strong>
          <button class="teams-btn teams-btn--primary teams-btn--compact" type="button" @click="novaConversa">
            <i class="plus icon"></i> Nova
          </button>
        </div>
        <div v-if="carregandoListaConversas" class="sidebar-loading">
          <div class="ui active inline loader small"></div>
        </div>
        <div v-else-if="listaConversas.length === 0" class="sidebar-empty">
          Nenhuma conversa ainda.
        </div>
        <div v-else class="sidebar-list">
          <button
            v-for="item in listaConversas"
            :key="item.id"
            type="button"
            class="sidebar-item"
            :class="{ 'sidebar-item--active': item.id === conversaAtivaId }"
            @click="selecionarConversa(item)"
          >
            <span class="sidebar-item__titulo">{{ item.titulo }}</span>
            <span class="sidebar-item__data">{{ formatarDataConversa(item.updated_at) }}</span>
          </button>
        </div>
      </aside>

      <div class="teams-assistente__main">
        <header class="teams-assistente__header">
          <div class="header-actions">
            <button
              class="teams-btn teams-btn--icon"
              type="button"
              :title="painelConversasAberto ? 'Ocultar conversas' : 'Mostrar conversas'"
              @click="painelConversasAberto = !painelConversasAberto"
            >
              <i class="list icon"></i>
            </button>
            <div class="header-info">
              <h3>{{ conversaTitulo || 'Mapa Sucesso Cliente' }}</h3>
              <span v-if="usuario.nome || usuario.email" class="header-sub">
                {{ usuario.nome || usuario.email }}
              </span>
            </div>
          </div>
          <div class="header-buttons">
            <button class="teams-btn teams-btn--ghost" type="button" @click="novaConversa">
              Nova conversa
            </button>
            <button class="teams-btn teams-btn--ghost" type="button" @click="sair">
              Sair
            </button>
          </div>
        </header>

        <div ref="mensagens" class="teams-assistente__messages">
          <div v-if="historico.length === 0 && !carregandoConversa" class="empty-state">
            <i class="lightbulb outline huge icon"></i>
            <p>Pergunte sobre clientes, faturamento, NPS ou evolucao do mapa de sucesso.</p>
          </div>

          <div
            v-for="(msg, index) in historico"
            :key="`${index}-${msg.role}`"
            class="message-row"
            :class="msg.role === 'user' ? 'message-row--user' : 'message-row--assistant'"
          >
            <div class="message-bubble">
              <div
                class="message-content"
                :class="{ 'message-content--markdown': msg.role === 'assistant' }"
                v-html="formatarConteudo(msg.content, msg.role)"
              ></div>
            </div>
          </div>

          <div v-if="carregandoResposta" class="message-row message-row--assistant">
            <div class="message-bubble message-bubble--loading">
              <div class="ui active inline loader small"></div>
              <span>Analisando sua pergunta...</span>
            </div>
          </div>
        </div>

        <footer class="teams-assistente__input">
          <form class="teams-input-form" @submit.prevent="enviarPergunta">
            <div class="teams-input-bar">
              <input
                v-model="pergunta"
                class="teams-input-field"
                type="text"
                placeholder="Digite sua pergunta ou nome do cliente..."
                :disabled="carregandoResposta"
                @keydown.enter.exact.prevent="enviarPergunta"
              >
              <button
                class="teams-btn teams-btn--primary teams-input-submit"
                :class="{ 'teams-btn--loading': carregandoResposta }"
                type="submit"
                :disabled="!pergunta.trim() || carregandoResposta"
              >
                Enviar
              </button>
            </div>
          </form>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import * as teamsImport from '@microsoft/teams-js';
import * as markedImport from 'marked';
import teamsApi from '@/services/teamsApi';

function getTeamsSdk() {
  const sdk = teamsImport.default || teamsImport;
  return sdk.app ? sdk : teamsImport;
}

function getMarked() {
  const mod = markedImport.default || markedImport;
  if (typeof mod === 'function') {
    return mod;
  }
  if (typeof mod.marked === 'function') {
    return mod.marked;
  }
  if (typeof mod.parse === 'function') {
    return mod.parse;
  }
  return null;
}

const marked = getMarked();

if (marked && typeof marked.setOptions === 'function') {
  marked.setOptions({
    breaks: true,
    gfm: true,
    headerIds: false,
    mangle: false,
  });
}

const STORAGE_TOKEN = '@Teams:token';
const STORAGE_EXPIRES = '@Teams:expires_at';
const STORAGE_USUARIO = '@Teams:usuario';
const STORAGE_CONVERSA = '@Teams:conversa_id';
const STORAGE_REQUISICAO = '@Teams:requisicao_id';
const POLL_INTERVAL_MS = 2500;
const POLL_TIMEOUT_MS = 300000;

export default {
  name: 'TeamsAssistente',
  data() {
    return {
      autenticado: false,
      carregandoInicial: true,
      carregandoConversa: false,
      carregandoResposta: false,
      acessoNegado: false,
      sessaoEncerrada: false,
      mensagemAcesso: '',
      teamsPronto: false,
      pergunta: '',
      historico: [],
      conversaAtivaId: null,
      conversaTitulo: '',
      listaConversas: [],
      painelConversasAberto: true,
      carregandoListaConversas: false,
      usuario: {},
      pollTimer: null,
      teamsContext: {
        theme: 'default',
        locale: 'pt-BR',
        userDisplayName: '',
        userEmail: '',
        userId: '',
        tenantId: '',
      },
    };
  },
  computed: {
    themeClass() {
      if (this.teamsContext.theme === 'dark') {
        return 'teams-assistente--dark';
      }
      if (this.teamsContext.theme === 'contrast') {
        return 'teams-assistente--contrast';
      }
      return 'teams-assistente--light';
    },
  },
  async mounted() {
    document.documentElement.classList.add('teams-assistente-page');
    document.body.classList.add('teams-assistente-page');

    await this.inicializarTeams();

    if (this.restaurarSessao()) {
      await this.carregarListaConversas();
      await this.carregarConversa(this.conversaAtivaId);
      await this.retomarPerguntaEmAndamento();
      this.carregandoInicial = false;
      return;
    }

    if (!this.teamsPronto) {
      this.carregandoInicial = false;
      return;
    }

    await this.autenticarComTeams();
    this.carregandoInicial = false;
  },
  beforeDestroy() {
    this.pararPollingResposta();
    document.documentElement.classList.remove('teams-assistente-page');
    document.body.classList.remove('teams-assistente-page');
  },
  methods: {
    async inicializarTeams() {
      try {
        const teams = getTeamsSdk();
        await teams.app.initialize();
        const ctx = await teams.app.getContext();
        const userEmail = (
          ctx.user?.userPrincipalName
          || ctx.user?.loginHint
          || ''
        ).trim().toLowerCase();
        const userId = (ctx.user?.id || userEmail).trim();

        this.teamsContext = {
          theme: ctx.app?.theme || 'default',
          locale: ctx.app?.locale || 'pt-BR',
          userDisplayName: ctx.user?.displayName || '',
          userEmail,
          userId,
          tenantId: ctx.user?.tenant?.id || ctx.tenant?.id || '',
        };
        this.teamsPronto = Boolean(userEmail);
        teams.app.registerOnThemeChangeHandler((theme) => {
          this.teamsContext.theme = theme || 'default';
        });
        teams.app.notifySuccess();
      } catch (e) {
        this.teamsPronto = false;
      }
    },
    restaurarSessao() {
      const token = sessionStorage.getItem(STORAGE_TOKEN);
      const expiresAt = sessionStorage.getItem(STORAGE_EXPIRES);
      const usuarioRaw = sessionStorage.getItem(STORAGE_USUARIO);
      const conversaSalva = sessionStorage.getItem(STORAGE_CONVERSA);

      if (!token || !expiresAt) {
        return false;
      }

      if (new Date(expiresAt) <= new Date()) {
        this.limparSessao();
        return false;
      }

      this.autenticado = true;
      this.usuario = usuarioRaw ? JSON.parse(usuarioRaw) : {};
      this.conversaAtivaId = conversaSalva ? Number(conversaSalva) : null;

      return true;
    },
    async autenticarComTeams() {
      try {
        const { data } = await teamsApi.post('/api/teams/auth/login', {
          teams_user_id: this.teamsContext.userId,
          teams_user_email: this.teamsContext.userEmail,
          teams_user_name: this.teamsContext.userDisplayName || undefined,
        });

        sessionStorage.setItem(STORAGE_TOKEN, data.token);
        sessionStorage.setItem(STORAGE_EXPIRES, data.expires_at);
        sessionStorage.setItem(STORAGE_USUARIO, JSON.stringify(data.usuario || {}));

        this.usuario = data.usuario || {};
        this.autenticado = true;
        await this.carregarListaConversas();
        await this.carregarConversa(this.conversaAtivaId);
        await this.retomarPerguntaEmAndamento();
      } catch (error) {
        if (error.response?.status === 403) {
          this.acessoNegado = true;
          this.mensagemAcesso = error.response?.data?.message
            || 'Seu e-mail nao esta na lista de acesso do assistente.';
          return;
        }

        this.acessoNegado = true;
        this.mensagemAcesso = error.response?.data?.message || 'Nao foi possivel autenticar.';
      }
    },
    async carregarListaConversas() {
      this.carregandoListaConversas = true;
      try {
        const { data } = await teamsApi.get('/api/teams/conversas');
        this.listaConversas = Array.isArray(data.conversas) ? data.conversas : [];
        this.sincronizarTituloConversaAtiva();
      } catch (error) {
        if (error.response?.status === 401) {
          this.limparSessao();
          if (this.teamsPronto) {
            await this.autenticarComTeams();
          }
        }
      } finally {
        this.carregandoListaConversas = false;
      }
    },
    aplicarConversa(data) {
      this.conversaAtivaId = data.id || null;
      this.conversaTitulo = data.titulo || 'Conversa';
      this.historico = Array.isArray(data.historico) ? data.historico : [];
      if (this.conversaAtivaId) {
        sessionStorage.setItem(STORAGE_CONVERSA, String(this.conversaAtivaId));
      }
    },
    async carregarConversa(conversaId = null) {
      this.carregandoConversa = true;
      try {
        const params = conversaId ? { conversa_id: conversaId } : {};
        const { data } = await teamsApi.get('/api/teams/conversa', { params });
        this.aplicarConversa(data);
        this.$nextTick(() => this.scrollParaFim());
      } catch (error) {
        if (error.response?.status === 401) {
          this.limparSessao();
          if (this.teamsPronto) {
            await this.autenticarComTeams();
          }
          return;
        }

        if (error.response?.status === 404 && conversaId) {
          sessionStorage.removeItem(STORAGE_CONVERSA);
          this.conversaAtivaId = null;
          await this.carregarConversa();
        }
      } finally {
        this.carregandoConversa = false;
      }
    },
    async novaConversa() {
      if (this.carregandoResposta) {
        return;
      }
      this.pararPollingResposta();
      try {
        const { data } = await teamsApi.post('/api/teams/conversas');
        this.aplicarConversa(data);
        await this.carregarListaConversas();
        this.$nextTick(() => this.scrollParaFim());
      } catch (error) {
        const msg = error.response?.data?.message || 'Nao foi possivel criar conversa.';
        this.historico = [{ role: 'assistant', content: msg }];
      }
    },
    async selecionarConversa(item) {
      if (!item || item.id === this.conversaAtivaId || this.carregandoResposta) {
        return;
      }
      this.pararPollingResposta();
      await this.carregarConversa(item.id);
    },
    sincronizarTituloConversaAtiva() {
      if (!this.conversaAtivaId) {
        return;
      }
      const ativa = this.listaConversas.find((c) => c.id === this.conversaAtivaId);
      if (ativa && ativa.titulo) {
        this.conversaTitulo = ativa.titulo;
      }
    },
    formatarDataConversa(iso) {
      if (!iso) {
        return '';
      }
      try {
        return new Date(iso).toLocaleString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        });
      } catch (e) {
        return '';
      }
    },
    async enviarPergunta() {
      const texto = this.pergunta.trim();
      if (!texto || this.carregandoResposta) {
        return;
      }

      this.historico.push({ role: 'user', content: texto });
      this.pergunta = '';
      this.carregandoResposta = true;
      this.$nextTick(() => this.scrollParaFim());

      try {
        const payload = { pergunta: texto };
        if (this.conversaAtivaId) {
          payload.conversa_id = this.conversaAtivaId;
        }
        const { data } = await teamsApi.post('/api/teams/perguntar', payload);
        const requisicaoId = data.requisicao_id || data.requisicaoId;

        if (data.conversa_id) {
          this.conversaAtivaId = data.conversa_id;
          sessionStorage.setItem(STORAGE_CONVERSA, String(data.conversa_id));
        }

        if (data.processando && requisicaoId) {
          sessionStorage.setItem(STORAGE_REQUISICAO, requisicaoId);
          await this.aguardarRespostaPergunta(requisicaoId);
          return;
        }

        const resposta = data.resposta || data.message || 'Sem resposta.';
        this.historico.push({ role: 'assistant', content: resposta });
        await this.carregarListaConversas();
      } catch (error) {
        const msg = error.response?.data?.message || 'Erro ao processar pergunta.';
        this.historico.push({ role: 'assistant', content: msg });
      } finally {
        this.carregandoResposta = false;
        this.$nextTick(() => this.scrollParaFim());
      }
    },
    async retomarPerguntaEmAndamento() {
      if (!this.conversaAtivaId || this.carregandoResposta) {
        return;
      }

      const requisicaoSalva = sessionStorage.getItem(STORAGE_REQUISICAO);

      try {
        if (requisicaoSalva) {
          this.carregandoResposta = true;
          await this.aguardarRespostaPergunta(requisicaoSalva, true);
          return;
        }

        const { data } = await teamsApi.get('/api/teams/perguntar/em-andamento', {
          params: { conversa_id: this.conversaAtivaId },
        });

        if (data.processando && (data.requisicao_id || data.requisicaoId)) {
          const requisicaoId = data.requisicao_id || data.requisicaoId;
          sessionStorage.setItem(STORAGE_REQUISICAO, requisicaoId);
          this.carregandoResposta = true;
          await this.aguardarRespostaPergunta(requisicaoId, true);
        }
      } catch (error) {
        if (error.response?.status === 401) {
          this.limparSessao();
        }
      } finally {
        this.carregandoResposta = false;
        this.$nextTick(() => this.scrollParaFim());
      }
    },
    async aguardarRespostaPergunta(requisicaoId, consultaImediata = false) {
      const inicio = Date.now();

      const consultar = async () => {
        if (Date.now() - inicio >= POLL_TIMEOUT_MS) {
          sessionStorage.removeItem(STORAGE_REQUISICAO);
          this.historico.push({
            role: 'assistant',
            content: 'A resposta demorou além do esperado. Tente enviar a pergunta novamente.',
          });
          return;
        }

        if (!consultaImediata) {
          await this.esperar(POLL_INTERVAL_MS);
        }
        consultaImediata = false;

        try {
          const { data } = await teamsApi.get(`/api/teams/perguntar/${requisicaoId}`);

          if (data.status === 'concluida') {
            sessionStorage.removeItem(STORAGE_REQUISICAO);
            if (Array.isArray(data.historico) && data.historico.length) {
              this.historico = data.historico;
            } else {
              const resposta = data.resposta || 'Sem resposta.';
              this.historico.push({ role: 'assistant', content: resposta });
            }
            if (data.conversa_id) {
              this.conversaAtivaId = data.conversa_id;
              sessionStorage.setItem(STORAGE_CONVERSA, String(data.conversa_id));
            }
            await this.carregarListaConversas();
            return;
          }

          if (data.status === 'erro') {
            sessionStorage.removeItem(STORAGE_REQUISICAO);
            if (Array.isArray(data.historico) && data.historico.length) {
              this.historico = data.historico;
            } else {
              this.historico.push({
                role: 'assistant',
                content: data.message || 'Erro ao processar pergunta.',
              });
            }
            await this.carregarListaConversas();
            return;
          }
        } catch (error) {
          if (error.response?.status === 401) {
            this.limparSessao();
            throw error;
          }

          if (error.response?.status === 404) {
            sessionStorage.removeItem(STORAGE_REQUISICAO);
            this.historico.push({
              role: 'assistant',
              content: 'Requisicao nao encontrada. Tente enviar a pergunta novamente.',
            });
            return;
          }
        }

        await consultar();
      };

      await consultar();
    },
    esperar(ms) {
      return new Promise((resolve) => {
        this.pollTimer = setTimeout(resolve, ms);
      });
    },
    pararPollingResposta() {
      if (this.pollTimer) {
        clearTimeout(this.pollTimer);
        this.pollTimer = null;
      }
    },
    async sair() {
      try {
        await teamsApi.post('/api/teams/auth/logout');
      } catch (e) {
        // ignore
      }
      this.limparSessao();
      this.sessaoEncerrada = true;
    },
    recarregarAba() {
      window.location.reload();
    },
    limparSessao() {
      sessionStorage.removeItem(STORAGE_TOKEN);
      sessionStorage.removeItem(STORAGE_EXPIRES);
      sessionStorage.removeItem(STORAGE_USUARIO);
      sessionStorage.removeItem(STORAGE_CONVERSA);
      sessionStorage.removeItem(STORAGE_REQUISICAO);
      this.autenticado = false;
      this.usuario = {};
      this.historico = [];
      this.conversaAtivaId = null;
      this.conversaTitulo = '';
      this.listaConversas = [];
    },
    scrollParaFim() {
      const el = this.$refs.mensagens;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    },
    formatarConteudo(texto, role = 'user') {
      if (!texto) {
        return '';
      }

      if (role === 'assistant') {
        if (typeof marked === 'function') {
          return marked(String(texto));
        }
        return String(texto)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/\n/g, '<br>');
      }

      return String(texto)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n/g, '<br>');
    },
  },
};
</script>

<style scoped>
.teams-assistente {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f4f6f9;
  color: #1f2937;
  font-family: 'Lato', 'Segoe UI', sans-serif;
}

.teams-assistente--dark {
  background: #201f1f;
  color: #f3f2f1;
}

.teams-assistente--contrast {
  background: #000;
  color: #fff;
}

.teams-assistente__status {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 1.5rem;
  gap: 1rem;
  text-align: center;
}

.status-card {
  width: 100%;
  max-width: 480px;
  padding: 1.5rem;
  border-radius: 12px;
}

.teams-assistente__chat {
  display: flex;
  flex-direction: row;
  height: 100vh;
  min-height: 100vh;
}

.teams-assistente__sidebar {
  width: 260px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-right: 1px solid #e5e7eb;
  overflow: hidden;
}

.teams-assistente--dark .teams-assistente__sidebar {
  background: #292828;
  border-right-color: #3b3a39;
}

.sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.85rem 1rem;
  border-bottom: 1px solid #e5e7eb;
}

.teams-assistente--dark .sidebar-header {
  border-bottom-color: #3b3a39;
}

.sidebar-loading,
.sidebar-empty {
  padding: 1rem;
  font-size: 0.9rem;
  opacity: 0.7;
}

.sidebar-list {
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.sidebar-item {
  width: 100%;
  text-align: left;
  border: 1px solid transparent;
  border-radius: 8px;
  background: transparent;
  padding: 0.65rem 0.75rem;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  color: inherit;
}

.teams-assistente--dark .sidebar-item {
  color: #f3f2f1;
}

.sidebar-item:hover {
  background: rgba(0, 120, 212, 0.08);
}

.teams-assistente--dark .sidebar-item:hover {
  background: rgba(98, 175, 255, 0.12);
}

.sidebar-item--active {
  background: rgba(0, 120, 212, 0.12);
  border-color: rgba(0, 120, 212, 0.35);
}

.teams-assistente--dark .sidebar-item--active {
  background: rgba(98, 175, 255, 0.18);
  border-color: rgba(98, 175, 255, 0.45);
}

.sidebar-item__titulo {
  font-size: 0.92rem;
  font-weight: 600;
  line-height: 1.3;
  word-break: break-word;
}

.sidebar-item__data {
  font-size: 0.75rem;
  opacity: 0.65;
}

.teams-assistente__main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.teams-assistente__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
}

.header-buttons {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}

.teams-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  min-height: 2rem;
  padding: 0.45rem 0.85rem;
  border-radius: 6px;
  border: 1px solid #c8c6c4;
  background: #fff;
  color: #323130;
  font-size: 0.85rem;
  font-weight: 600;
  line-height: 1.2;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
  white-space: nowrap;
}

.teams-btn:hover {
  background: #f3f2f1;
  border-color: #a19f9d;
}

.teams-btn:focus {
  outline: 2px solid rgba(0, 120, 212, 0.45);
  outline-offset: 1px;
}

.teams-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

.teams-btn--compact {
  min-height: 1.85rem;
  padding: 0.35rem 0.65rem;
  font-size: 0.8rem;
}

.teams-btn--icon {
  min-width: 2rem;
  width: 2rem;
  height: 2rem;
  padding: 0;
  flex-shrink: 0;
}

.teams-btn--icon .icon {
  margin: 0 !important;
  opacity: 0.9;
}

.teams-btn--ghost {
  background: transparent;
}

.teams-btn--primary {
  background: #0078d4;
  border-color: #0078d4;
  color: #fff;
}

.teams-btn--primary:hover {
  background: #106ebe;
  border-color: #106ebe;
  color: #fff;
}

.teams-btn--loading {
  opacity: 0.75;
  pointer-events: none;
}

.teams-assistente--dark .teams-assistente__header {
  background: #292828;
  border-bottom-color: #3b3a39;
}

.teams-assistente--dark .teams-btn {
  background: #3b3a39;
  border-color: #605e5c;
  color: #f3f2f1;
}

.teams-assistente--dark .teams-btn:hover {
  background: #484644;
  border-color: #8a8886;
  color: #fff;
}

.teams-assistente--dark .teams-btn--ghost {
  background: transparent;
  border-color: #8a8886;
  color: #f3f2f1;
}

.teams-assistente--dark .teams-btn--ghost:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: #c8c6c4;
  color: #fff;
}

.teams-assistente--dark .teams-btn--primary {
  background: #2899f5;
  border-color: #2899f5;
  color: #201f1f;
}

.teams-assistente--dark .teams-btn--primary:hover {
  background: #62afff;
  border-color: #62afff;
  color: #201f1f;
}

.teams-assistente--contrast .teams-btn {
  background: #000;
  border-color: #fff;
  color: #fff;
}

.teams-assistente--contrast .teams-btn--ghost {
  background: transparent;
}

.teams-assistente--contrast .teams-btn--primary {
  background: #fff;
  border-color: #fff;
  color: #000;
}

.header-info h3 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
}

.header-sub {
  font-size: 0.85rem;
  opacity: 0.75;
}

.teams-assistente__messages {
  flex: 1;
  overflow-y: auto;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.empty-state {
  margin: auto;
  text-align: center;
  max-width: 320px;
  opacity: 0.7;
}

.message-row {
  display: flex;
}

.message-row--user {
  justify-content: flex-end;
}

.message-row--assistant {
  justify-content: flex-start;
}

.message-bubble {
  max-width: 85%;
  padding: 0.85rem 1rem;
  border-radius: 14px;
  line-height: 1.5;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
}

.message-row--user .message-bubble {
  background: #0078d4;
  color: #fff;
  border-bottom-right-radius: 4px;
}

.message-row--assistant .message-bubble {
  background: #fff;
  color: #1f2937;
  border-bottom-left-radius: 4px;
}

.teams-assistente--dark .message-row--assistant .message-bubble {
  background: #323130;
  color: #f3f2f1;
}

.message-bubble--loading {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.message-content--markdown >>> h1,
.message-content--markdown >>> h2,
.message-content--markdown >>> h3,
.message-content--markdown >>> h4 {
  font-size: 1rem;
  font-weight: 700;
  margin: 0.85rem 0 0.4rem;
  line-height: 1.35;
}

.message-content--markdown >>> h1:first-child,
.message-content--markdown >>> h2:first-child,
.message-content--markdown >>> h3:first-child,
.message-content--markdown >>> h4:first-child {
  margin-top: 0;
}

.message-content--markdown >>> p {
  margin: 0.35rem 0;
}

.message-content--markdown >>> ul,
.message-content--markdown >>> ol {
  margin: 0.35rem 0 0.5rem;
  padding-left: 1.35rem;
}

.message-content--markdown >>> li {
  margin: 0.2rem 0;
}

.message-content--markdown >>> strong {
  font-weight: 700;
}

.message-content--markdown >>> hr {
  border: none;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  margin: 0.75rem 0;
}

.teams-assistente--dark .message-content--markdown >>> hr {
  border-top-color: rgba(255, 255, 255, 0.2);
}

.teams-assistente__input {
  padding: 1rem 1.25rem;
  background: #fff;
  border-top: 1px solid #e5e7eb;
}

.teams-input-form {
  width: 100%;
}

.teams-input-bar {
  display: flex;
  align-items: stretch;
  gap: 0.5rem;
  width: 100%;
}

.teams-input-field {
  flex: 1;
  min-width: 0;
  height: 2.5rem;
  padding: 0.55rem 0.85rem;
  border: 1px solid #c8c6c4;
  border-radius: 6px;
  background: #fff;
  color: #323130;
  font-size: 0.95rem;
  line-height: 1.4;
  font-family: inherit;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
}

.teams-input-field::placeholder {
  color: #605e5c;
}

.teams-input-field:hover {
  border-color: #a19f9d;
}

.teams-input-field:focus {
  outline: none;
  border-color: #0078d4;
  box-shadow: 0 0 0 2px rgba(0, 120, 212, 0.25);
}

.teams-input-field:disabled {
  opacity: 0.65;
  cursor: not-allowed;
  background: #f3f2f1;
}

.teams-input-submit {
  min-height: 2.5rem;
  padding-left: 1.1rem;
  padding-right: 1.1rem;
  flex-shrink: 0;
}

.teams-assistente--dark .teams-assistente__input {
  background: #292828;
  border-top-color: #3b3a39;
}

.teams-assistente--dark .teams-input-field {
  background: #201f1f;
  border-color: #605e5c;
  color: #f3f2f1;
}

.teams-assistente--dark .teams-input-field::placeholder {
  color: #a19f9d;
}

.teams-assistente--dark .teams-input-field:hover {
  border-color: #8a8886;
  background: #252423;
}

.teams-assistente--dark .teams-input-field:focus {
  border-color: #62afff;
  box-shadow: 0 0 0 2px rgba(98, 175, 255, 0.28);
  background: #201f1f;
}

.teams-assistente--dark .teams-input-field:disabled {
  background: #2d2c2c;
  color: #a19f9d;
}

.teams-assistente--contrast .teams-input-field {
  background: #000;
  border-color: #fff;
  color: #fff;
}

.teams-assistente--contrast .teams-input-field::placeholder {
  color: #c8c6c4;
}

.teams-assistente--contrast .teams-input-field:focus {
  border-color: #fff;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.35);
}
</style>

<style>
html.teams-assistente-page,
body.teams-assistente-page {
  margin: 0;
  padding: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

body.teams-assistente-page #app {
  margin: 0;
  padding: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
}
</style>
